import StageQr from "@/components/StageQr";
import { Plus } from "lucide-react";
import EquipmentOptions from "@/components/EquipmentOptions";
import { Button } from "@/components/ui/button";

// "7. PACKING · Line Clearance · Packing / Approval / Dispatch Details", the paper form (page 09,
// PF005/F02-00), as the Packing stage's record. The stage's date, packed_by and finished_qty
// (which the Med Pack register and the COA / Dispatch stages read) follow the Packing Details table.
const MIN_ROWS = 2;
const PACK_KEYS = ["lot", "dt", "boxes", "per_box", "packed", "by"];
const APPROVAL_KEYS = ["lot", "dt", "grn", "qty", "sign"];
const DISPATCH_KEYS = ["lot", "dt", "gpn", "qty", "doc", "sign"];
const has = (v) => v !== undefined && v !== null && v !== "";
const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const fmt = (n) => (Math.round(n * 1000) / 1000).toLocaleString("en-IN", { maximumFractionDigits: 3 });
const arr = (v) => (Array.isArray(v) ? v : []);
const blank = (keys) => () => Object.fromEntries(keys.map((k) => [k, ""]));
const blankPack = blank(PACK_KEYS), blankApproval = blank(APPROVAL_KEYS), blankDispatch = blank(DISPATCH_KEYS);

// Fields of this stage the sheet covers; the rest stay available under "More packing details".
export const PACK_SHEET_FIELDS = new Set([
  "equipment_id", "previous_job_name", "previous_product_code", "lc_done_by", "lc_checked_by", "pack_rows", "approval_rows",
  "dispatch_rows", "total_boxes", "date", "packed_by", "finished_qty",
]);

/** Packing Details rows: as saved, or, for records made before the table existed, one row from the single figures. */
export function packRows(data) {
  if (arr(data.pack_rows).length) return data.pack_rows;
  if (!["finished_qty", "packed_by", "date"].some((k) => has(data[k]))) return [];
  return [{ lot: "", dt: data.date ?? "", boxes: "", per_box: "", packed: data.finished_qty ?? "", by: data.packed_by ?? "" }];
}

/** Approval Details rows; before any are saved, one starts from the raw material's lot and GRN (Stores
 * stage) plus the quantity QC already approved (Quality stage's "Approved Qty", Random Sampling). */
export function approvalRows(data, carried = {}) {
  if (arr(data.approval_rows).length) return data.approval_rows;
  if (!has(carried.lot_id) && !has(carried.grn_number) && !has(carried.an_approved_qty)) return [];
  return [{ ...blankApproval(), lot: carried.lot_id ?? "", grn: carried.grn_number ?? "", qty: carried.an_approved_qty ?? "" }];
}

export function packFigures(data) {
  const rows = packRows(data);
  return { rows, boxes: rows.reduce((s, r) => s + num(r.boxes), 0), packed: rows.reduce((s, r) => s + num(r.packed), 0) };
}

/** The stage fields to save for the current Packing Details: what the register, COA and Dispatch read.
 * `carried` is what Quality (and Stores, via the job's own carry-forward) handed down. */
export function packTotalsPatch(data, carried = {}) {
  const f = packFigures(data);
  if (!f.rows.length) return {};
  const first = f.rows.find((r) => has(r.by));
  const dated = f.rows.find((r) => has(r.dt));
  const patch = {
    finished_qty: f.packed, total_boxes: f.boxes,
    ...(first && { packed_by: first.by }),
    ...(dated && { date: String(dated.dt).slice(0, 10) }),
  };
  // Dispatch Qty is the same total just packed — its first row always tracks it live, same as
  // Finished Qty above, so it's never left blank (or stuck at a stray leftover number) to retype.
  const dispatches = arr(data.dispatch_rows);
  if (!dispatches.length) {
    patch.dispatch_rows = [{ ...blankDispatch(), qty: f.packed }];
  } else if (String(dispatches[0].qty ?? "") !== String(f.packed)) {
    patch.dispatch_rows = dispatches.map((r, i) => (i === 0 ? { ...r, qty: f.packed } : r));
  }
  // Approved Qty is Quality's own figure (Random Sampling) — its first row keeps tracking that live
  // too, so filling it in at Quality after this row already exists still reaches Packing.
  if (has(carried.an_approved_qty)) {
    const approvals = arr(data.approval_rows);
    if (!approvals.length) {
      patch.approval_rows = [{ ...blankApproval(), lot: carried.lot_id ?? "", grn: carried.grn_number ?? "", qty: carried.an_approved_qty }];
    } else if (String(approvals[0].qty ?? "") !== String(carried.an_approved_qty)) {
      patch.approval_rows = approvals.map((r, i) => (i === 0 ? { ...r, qty: carried.an_approved_qty } : r));
    }
  }
  return patch;
}

const line = "border border-neutral-700";
const cell = `${line} px-1.5 py-1 text-[11px] align-middle`;
const input = "w-full bg-transparent outline-none text-[11px] font-medium placeholder:text-neutral-300 focus:bg-orange-50 disabled:text-neutral-800";
const dotted = "border-b border-dotted border-neutral-600 pb-0.5";

/** `data` is the stage record, `ctx` its context (the job, what Slitting and Stores carried), `setMany(patch)` changes keys. */
export default function PackingSheet({ ctx, data, setMany, readOnly }) {
  const common = ctx?.common || {};
  const carried = ctx?.carried || {};
  const f = packFigures(data);
  const packs = [...f.rows, ...Array.from({ length: Math.max(MIN_ROWS - f.rows.length, 0) }, blankPack)];
  const apRows = approvalRows(data, carried);
  const approvals = [...apRows, ...Array.from({ length: Math.max(MIN_ROWS - apRows.length, 0) }, blankApproval)];
  const dispRows = arr(data.dispatch_rows);
  const dispatches = [...dispRows, ...Array.from({ length: Math.max(MIN_ROWS - dispRows.length, 0) }, blankDispatch)];

  const change = (patch) => setMany({ ...patch, ...packTotalsPatch({ ...data, ...patch }, carried) });
  const editList = (key, list, keys, kept) => (i, k, v) => {
    const next = list.map((r) => ({ ...r }));
    next[i][k] = v;
    return { [key]: next.filter((r, at) => at < kept || keys.some((x) => has(r[x]))) };
  };
  // The paper form's own shorthand for "Qty per Box": "4250 X 6" (6 rolls of 4250 each), sometimes
  // with a leftover part added on — "4250 X 6 + 1500". Each "+"-separated term is either an "A x B"
  // product or a bare number; their sum is that row's Packed Qty. Boxes is the box number/count,
  // entered separately and untouched by this.
  const TERM = /^(\d+(?:\.\d+)?)\s*(?:[xX*]\s*(\d+(?:\.\d+)?))?$/;
  const parseShorthand = (v) => {
    const terms = String(v).split("+").map((t) => t.trim()).filter(Boolean);
    if (!terms.length) return null;
    let total = 0;
    for (const t of terms) {
      const m = t.match(TERM);
      if (!m) return null;
      total += Number(m[1]) * (m[2] !== undefined ? Number(m[2]) : 1);
    }
    return total;
  };
  const editPack = (i, k, v) => {
    const before = packs[i];
    const patch = editList("pack_rows", packs, PACK_KEYS, f.rows.length)(i, k, v);
    const row = patch.pack_rows[i];
    const shorthand = k === "per_box" && /[xX*+]/.test(v) ? parseShorthand(v) : null;
    if (shorthand !== null) {
      row.packed = shorthand;
    } else if (k === "boxes" || k === "per_box") {
      // Packed Qty = boxes x qty per box while it still holds that product (a short last box is typed over it).
      const oldAuto = num(before.boxes) * num(before.per_box);
      const auto = num(row.boxes) * num(row.per_box);
      if (auto > 0 && (!has(before.packed) || num(before.packed) === oldAuto)) row.packed = auto;
    }
    change(patch);
  };
  const editApproval = (i, k, v) => setMany(editList("approval_rows", approvals, APPROVAL_KEYS, apRows.length)(i, k, v));
  const editDispatch = (i, k, v) => setMany(editList("dispatch_rows", dispatches, DISPATCH_KEYS, dispRows.length)(i, k, v));

  // Render helpers, not components: a component defined here would remount on every keystroke.
  const text = (label, k) => (
    <div key={k} className="flex items-end gap-1.5">
      <span className="text-[11px] whitespace-nowrap">{label}</span>
      <input list={k === "equipment_id" ? "equipment-ids" : undefined} className={`${input} ${dotted}`} value={data[k] ?? ""} disabled={readOnly} onChange={(e) => setMany({ [k]: e.target.value })} data-testid={`pack-${k}`} />
    </div>
  );
  const cellInput = (testid, value, onChange, { type = "text", right = false } = {}) => (
    <td className={cell}>
      <input type={type} className={`${input} ${right ? "text-right font-mono-num" : ""}`} value={value ?? ""} disabled={readOnly}
        onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </td>
  );
  const head = (labels) => (
    <thead><tr className="align-top">{labels.map((h) => <th key={h} className={`${cell} text-left font-semibold leading-tight`}>{h}</th>)}</tr></thead>
  );
  const addRow = (onClick, testid) => !readOnly && (
    <Button type="button" variant="outline" size="sm" className="no-print rounded-sm h-6 gap-1 text-[11px]" onClick={onClick} data-testid={testid}>
      <Plus size={11} /> Add row
    </Button>
  );

  return (
    <div className="bg-white border border-neutral-700 text-neutral-900" data-testid="packing-sheet">
      <EquipmentOptions types={["Slitter", "Rewinder", "Cutter"]} />
      <div className="flex items-start justify-between px-3 pt-2 pb-1">
        <div>
          <div className="font-bold text-sm">MedPack India Pvt. Ltd.</div>
          <div className="print-only text-[10px] text-neutral-700">
            BPR {common.mpwo_number} · {[common.customer_name, common.product_name].filter(Boolean).join(" · ")}
          </div>
        </div>
        <div className="font-semibold text-[12px] border-b border-neutral-700 px-6">7. PACKING</div>
        <div className="flex items-start gap-2"><StageQr jobId={ctx.job_id} stage="packing" /><div className="text-[11px] text-neutral-600">Page 09 of 11</div></div>
      </div>

      <div className="px-3 pb-2 space-y-1.5">
        <div className={`relative ${line} rounded-sm pt-3 p-2 mt-2.5`}>
          <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-white px-2 text-[12px] font-bold underline whitespace-nowrap">Line Clearance</div>
          <div className="grid grid-cols-3 gap-x-4 gap-y-1.5">
            {text("Equipment I.D.:", "equipment_id")}
            <span />
            <span />
            {text("Previous Job Name:", "previous_job_name")}
            <div className="flex items-end gap-1.5">
              <span className="text-[11px] whitespace-nowrap">W/o No.:</span>
              <span className={`${dotted} flex-1 text-[11px] font-semibold font-mono-num`}>{common.mpwo_number}</span>
            </div>
            {text("Previous Product Code:", "previous_product_code")}
          </div>
        </div>
        <div className={`${line} text-[11px]`}>
          <div className="flex gap-3 p-1.5">
            <span className="font-semibold">7.1</span>
            <span>
              Clean the area before starting the job. All previous batch rolls must be packed and sealed with BOPP tape and clear the line for proceeding job.
              Discard the previous job remaining quantity of labels and films into dustbin. After completion of line clearance check the area and assure that the
              area is free of previous job material.
              <br />NOTE: If Line clearance does not complies, then repeat the process again
            </span>
          </div>
          <div className="grid grid-cols-2 gap-x-6 px-1.5 pb-1.5 pt-4 border-t border-neutral-700">
            {text("Done by & Date (Production)", "lc_done_by")}
            {text("Checked by & Date (Q.C.)", "lc_checked_by")}
          </div>
        </div>

        <div className="text-[11px] pt-1">Packing Details:( To be filled by packing supervisor)</div>
        <table className="w-full border-collapse" data-testid="pack-rows">
          {head(["LOT No.", "Date/Time", "Boxes", "Qty per Box", "Packed Qty", "Packed By"])}
          <tbody>
            {packs.map((r, i) => (
              <tr key={i} className="h-7">
                {cellInput(`pack-row-${i}-lot`, r.lot, (v) => editPack(i, "lot", v))}
                {cellInput(`pack-row-${i}-dt`, r.dt, (v) => editPack(i, "dt", v), { type: "datetime-local" })}
                {cellInput(`pack-row-${i}-boxes`, r.boxes, (v) => editPack(i, "boxes", v), { type: "number", right: true })}
                {cellInput(`pack-row-${i}-per_box`, r.per_box, (v) => editPack(i, "per_box", v), { type: "text", right: true })}
                {cellInput(`pack-row-${i}-packed`, r.packed, (v) => editPack(i, "packed", v), { type: "number", right: true })}
                {cellInput(`pack-row-${i}-by`, r.by, (v) => editPack(i, "by", v))}
              </tr>
            ))}
            {f.rows.length > 1 && (
              <tr className="bg-neutral-50 font-semibold">
                <td className={cell} colSpan={2}>Total</td>
                <td className={`${cell} text-right font-mono-num`} data-testid="pack-boxes-total">{fmt(f.boxes)}</td>
                <td className={cell} />
                <td className={`${cell} text-right font-mono-num`} data-testid="pack-packed-total">{fmt(f.packed)}</td>
                <td className={cell} />
              </tr>
            )}
          </tbody>
        </table>
        {addRow(() => change({ pack_rows: [...packs, blankPack()] }), "pack-add-pack")}

        <div className="text-[11px] pt-1">Approval Details:</div>
        <table className="w-full border-collapse" data-testid="pack-approvals">
          {head(["LOT No.", "Date/Time", "GRN No.", "Approved Qty", "Signature"])}
          <tbody>
            {approvals.map((r, i) => (
              <tr key={i} className="h-7">
                {cellInput(`pack-ap-${i}-lot`, r.lot, (v) => editApproval(i, "lot", v))}
                {cellInput(`pack-ap-${i}-dt`, r.dt, (v) => editApproval(i, "dt", v), { type: "datetime-local" })}
                {cellInput(`pack-ap-${i}-grn`, r.grn, (v) => editApproval(i, "grn", v))}
                {cellInput(`pack-ap-${i}-qty`, r.qty, (v) => editApproval(i, "qty", v), { type: "number", right: true })}
                {cellInput(`pack-ap-${i}-sign`, r.sign, (v) => editApproval(i, "sign", v))}
              </tr>
            ))}
          </tbody>
        </table>
        {addRow(() => setMany({ approval_rows: [...approvals, blankApproval()] }), "pack-add-approval")}

        <div className="text-[11px] pt-1">Dispatch Details:</div>
        <table className="w-full border-collapse" data-testid="pack-dispatches">
          {head(["LOT No.", "Date/Time", "GPN No.", "Dispatch Qty", "D.C No./Invoce No", "Signature"])}
          <tbody>
            {dispatches.map((r, i) => (
              <tr key={i} className="h-7">
                {cellInput(`pack-dp-${i}-lot`, r.lot, (v) => editDispatch(i, "lot", v))}
                {cellInput(`pack-dp-${i}-dt`, r.dt, (v) => editDispatch(i, "dt", v), { type: "datetime-local" })}
                {cellInput(`pack-dp-${i}-gpn`, r.gpn, (v) => editDispatch(i, "gpn", v))}
                {cellInput(`pack-dp-${i}-qty`, r.qty, (v) => editDispatch(i, "qty", v), { type: "number", right: true })}
                {cellInput(`pack-dp-${i}-doc`, r.doc, (v) => editDispatch(i, "doc", v))}
                {cellInput(`pack-dp-${i}-sign`, r.sign, (v) => editDispatch(i, "sign", v))}
              </tr>
            ))}
          </tbody>
        </table>
        {addRow(() => setMany({ dispatch_rows: [...dispatches, blankDispatch()] }), "pack-add-dispatch")}
      </div>
      <div className="px-3 pb-2 text-[10px] text-neutral-600">FORMAT No. PF005/F02-00</div>
    </div>
  );
}
